import React from "react";
import {
  FaCheck,
  FaUsers,
  FaCloud,
  FaShieldAlt,
  FaHeadset,
  FaBullhorn,
  FaBolt,
  FaRegCalendarCheck,
  FaChartLine,
  FaRegLightbulb,
  FaRegClock,
  FaRegStar,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../components/AuthContext";

const Business = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [exchangeRate, setExchangeRate] = React.useState(1470);

  const handlePlanSelection = () => {
    const plan = {
      name: 'Business Standard',
      price: '12.60',
      isAnnual: true // Business 페이지는 기본적으로 1년 약정 가격을 보여줌
    };

    if (!user) {
      navigate("/login", { state: { fromPricing: true, selectedPlan: plan } });
    } else {
      navigate("/payment", { state: { fromPricing: true, selectedPlan: plan } });
    }
  };

  React.useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD');
        const data = await response.json();
        if (data && data.rates && data.rates.KRW) {
          setExchangeRate(Math.round(data.rates.KRW));
        }
      } catch (error) {
        console.error("Exchange rate fetch error:", error);
      }
    };
    fetchExchangeRate();
  }, []);

  const formatKRW = (usdPrice) => {
    const krw = Math.round(parseFloat(usdPrice) * exchangeRate);
    return new Intl.NumberFormat('ko-KR', { style: 'currency', currency: 'KRW' }).format(krw);
  };

  const features = [
// ... (rest of features stays the same)
    {
      title: "기본 기능",
      icon: <FaBolt className="text-blue-500 text-xl mb-2" />,
      description: "업무 효율성을 극대화하는 필수 도구",
      items: [
        { text: "맞춤 비즈니스 이메일", highlight: "(yourbusiness.com)" },
        { text: "화상 회의", highlight: "(최대 150명, 녹화 기능 포함)" },
        { text: "보안 메시지 및 채팅", highlight: "" },
        { text: "공유 캘린더 및 일정 관리", highlight: "" },
        { text: "클라우드 스토리지", highlight: "(사용자당 30GB)" },
        { text: "문서/스프레드시트/프레젠테이션", highlight: "" },
        { text: "공유 드라이브", highlight: "(팀 공유 스토리지)" },
      ],
    },
    {
      title: "보안 및 관리",
      icon: <FaShieldAlt className="text-blue-500 text-xl mb-2" />,
      description: "데이터 보호와 간편한 제어 기능",
      items: [
        { text: "관리자 제어 기능", highlight: "" },
        { text: "기업급 보안 시스템", highlight: "" },
        { text: "2단계 인증", highlight: "(향상된 보안)" },
        { text: "엔드포인트 관리", highlight: "(모바일 기기 포함)" },
        { text: "감사 로그 및 알림", highlight: "" },
        { text: "이메일 보관 및 복구", highlight: "" },
      ],
    },
    {
      title: "지원 및 서비스",
      icon: <FaHeadset className="text-blue-500 text-xl mb-2" />,
      description: "안정적인 업무 환경을 위한 백업 체계",
      items: [
        { text: "24/7 온라인 고객 지원", highlight: "" },
        { text: "전화 및 이메일 지원", highlight: "(한국어 지원)" },
        { text: "맞춤형 배포 서비스", highlight: "" },
        { text: "온라인 교육 자료", highlight: "(가이드 및 튜토리얼)" },
      ],
    },
  ];

  const benefits = [
    {
      icon: <FaRegLightbulb />,
      title: "생산성 향상",
      description:
        "모든 업무 도구를 하나의 플랫폼에서 활용하여 30% 이상의 시간 절약",
    },
    {
      icon: <FaUsers />,
      title: "팀 협업 강화",
      description: "실시간 문서 공동 작업과 화상 회의로 효율적인 팀워크 구축",
    },
    {
      icon: <FaCloud />,
      title: "안전한 데이터 관리",
      description: "엔터프라이즈급 보안으로 중요 비즈니스 데이터 보호",
    },
    {
      icon: <FaRegClock />,
      title: "시간 절약",
      description: "자동화된 워크플로우로 반복 작업 최소화",
    },
  ];

  const faqs = [
    {
      question: "다른 플랜과 어떤 차이가 있나요?",
      answer:
        "Business Standard는 중소기업에 최적화된 플랜으로, Business Starter보다 더 많은 스토리지와 참석자 수를 제공하며, Enterprise 플랜보다는 경제적입니다.",
    },
    {
      question: "라이선스는 어떻게 구매하나요?",
      answer:
        "무료 체험 후 원하는 사용자 수만큼 라이선스를 구매할 수 있으며, 필요에 따라 언제든지 조정 가능합니다.",
    },
  ];

  return (
    <div className="bg-gradient-to-b from-gray-50 to-white py-16 px-4 sm:px-6 lg:px-8">
      {/* 헤더 섹션 */}
      <div className="max-w-7xl mx-auto text-center mb-16">
        <div className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full font-medium text-sm mb-4">
          Google Workspace
        </div>
        <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent mb-6">
          Business Standard
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          중소기업을 위한 완벽한 비즈니스 솔루션으로, 협업 효율성과 데이터
          보안을 한 단계 높여보세요.
        </p>

        {/* 핵심 혜택 배지 */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          {["전문가 지원", "향상된 보안", "무제한 협업", "화상회의 녹화"].map(
            (badge, index) => (
              <span
                key={index}
                className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium"
              >
                {badge}
              </span>
            )
          )}
        </div>
        <div className="mt-8 flex justify-center">
          <p className="text-blue-700 text-sm font-medium bg-blue-50 px-4 py-1.5 rounded-full border border-blue-100 flex items-center">
            <FaUsers className="mr-2" /> 1년 약정 및 10명 이상 계약 시 적용되는 가격입니다
          </p>
        </div>
      </div>

      {/* 2열 레이아웃: 가격 + 주요 장점 */}
      <div className="max-w-7xl mx-auto mb-16 grid md:grid-cols-2 gap-8 items-center">
        {/* 가격 섹션 */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl hover:-translate-y-1">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 text-white">
            <div className="text-lg font-medium">Business Standard 플랜</div>
          </div>
          <div className="p-8">
            <div className="flex items-baseline gap-2 mb-1 flex-wrap">
              <div className="text-5xl font-bold text-gray-900">$12.60</div>
              <div className="text-xl text-gray-500 pb-1">USD</div>
              <div className="text-2xl font-bold text-blue-600 ml-auto">({formatKRW(12.60)})</div>
            </div>
            <div className="text-gray-600 mb-3">사용자당 매월, 1년 약정 기준</div>

            <div className="bg-green-50 text-green-700 px-4 py-2 rounded-lg text-sm font-medium mb-2 inline-flex items-center w-full justify-center">
              <FaRegStar className="mr-2" /> 10% 할인 적용{" "}
              <span className="text-gray-400 ml-2 line-through">$14 USD</span>
            </div>
            <p className="text-blue-600 text-[11px] font-bold text-center mb-6">
              * 1년 약정 및 10명 이상 계약 시 적용가 (실시간 환율 {new Intl.NumberFormat('ko-KR').format(exchangeRate)}원 기준)
            </p>


            <div className="space-y-4 mb-6">
              <div className="flex items-center">
                <FaCheck className="text-green-500 mr-3" />
                <span className="text-gray-700">무제한 앱 및 서비스 접근</span>
              </div>
              <div className="flex items-center">
                <FaCheck className="text-green-500 mr-3" />
                <span className="text-gray-700">사용자당 2TB 스토리지</span>
              </div>
              <div className="flex items-center">
                <FaCheck className="text-green-500 mr-3" />
                <span className="text-gray-700">화상회의 최대 150명</span>
              </div>
            </div>

            <button 
              onClick={handlePlanSelection}
              className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              지금 구독하고 시작하기
            </button>
            <p className="text-sm text-gray-500 mt-4 text-center">
              신용카드 정보 불필요, 언제든지 취소 가능
            </p>
          </div>
        </div>

        {/* 주요 장점 섹션 */}
        <div className="bg-gray-50 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">
            왜 Business Standard인가요?
          </h3>
          <div className="space-y-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex">
                <div className="text-blue-500 text-xl mt-1 mr-4">
                  {benefit.icon}
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-1">
                    {benefit.title}
                  </h4>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 특징 섹션 - 탭 스타일 카드 */}
      <div className="max-w-7xl mx-auto mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
          모든 기능을 한눈에
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((section, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
            >
              <div className="border-b p-6">
                <div className="flex items-center justify-between mb-4">
                  {section.icon}
                  <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-full text-xs font-medium">
                    비즈니스 필수
                  </span>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {section.title}
                </h3>
                <p className="text-gray-600 text-sm">{section.description}</p>
              </div>
              <div className="p-6">
                <ul className="space-y-4">
                  {section.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <FaCheck className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                      <div>
                        <span className="text-gray-700">{item.text}</span>
                        {item.highlight && (
                          <span className="text-blue-600 font-medium">
                            {" "}
                            {item.highlight}
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 비교 테이블 */}
     <div className="max-w-7xl mx-auto mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
          플랜 비교
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full bg-white rounded-xl shadow-lg">
            <thead>
              <tr className="bg-gray-50">
                <th className="py-4 px-6 text-left text-gray-600 font-medium">
                  기능
                </th>
                <th className="py-4 px-6 text-center text-gray-600 font-medium">
                  Starter
                </th>
                <th className="py-4 px-6 text-center text-blue-600 font-bold bg-blue-50">
                  <span className="inline-block px-2 py-1 rounded-full bg-blue-600 text-white text-xs mb-1">
                    추천
                  </span>
                  <div>Business Standard</div>
                </th>
                <th className="py-4 px-6 text-center text-gray-600 font-medium">
                  Business Plus
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="py-4 px-6 text-gray-700">클라우드 스토리지</td>
                <td className="py-4 px-6 text-center text-gray-600">30GB</td>
                <td className="py-4 px-6 text-center font-medium text-blue-700 bg-blue-50">
                  2TB
                </td>
                <td className="py-4 px-6 text-center text-gray-600">5TB</td>
              </tr>
              <tr>
                <td className="py-4 px-6 text-gray-700">화상회의 (참석자)</td>
                <td className="py-4 px-6 text-center text-gray-600">100명</td>
                <td className="py-4 px-6 text-center font-medium text-blue-700 bg-blue-50">
                  150명 + 녹화
                </td>
                <td className="py-4 px-6 text-center text-gray-600">
                  500명 + 녹화
                </td>
              </tr>
              <tr>
                <td className="py-4 px-6 text-gray-700">보안 및 관리</td>
                <td className="py-4 px-6 text-center text-gray-600">표준 보안</td>
                <td className="py-4 px-6 text-center font-medium text-blue-700 bg-blue-50">
                  표준 보안
                </td>
                <td className="py-4 px-6 text-center text-gray-600">향상된 보안</td>
              </tr>
              <tr>
                <td className="py-4 px-6 text-gray-700">가격 (월/사용자)</td>
                <td className="py-4 px-6 text-center text-gray-600">
                  <div>$7.00 USD</div>
                  <div className="text-[10px] text-blue-600 font-bold">({formatKRW(7.00)})</div>
                </td>
                <td className="py-4 px-6 text-center font-medium text-blue-700 bg-blue-50">
                  <div>$12.60 USD</div>
                  <div className="text-[10px] text-blue-600 font-bold">({formatKRW(12.60)})</div>
                </td>
                <td className="py-4 px-6 text-center text-gray-600">
                  <div>$22.00 USD</div>
                  <div className="text-[10px] text-blue-600 font-bold">({formatKRW(22.00)})</div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* FAQ 섹션 */}
      <div className="max-w-3xl mx-auto mb-16">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
          자주 묻는 질문
        </h2>
        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div key={index} className="bg-white rounded-xl shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                {faq.question}
              </h3>
              <p className="text-gray-600">{faq.answer}</p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA 섹션 */}
      <div className="max-w-5xl mx-auto mt-16 text-center">
        <div className="bg-gradient-to-r from-blue-500 to-indigo-600 rounded-2xl p-10 shadow-xl text-white">
          <h2 className="text-3xl font-bold mb-4 text-white">
            지금 바로 비즈니스를 혁신하세요
          </h2>
          <p className="text-xl opacity-90 mb-8 w-full mx-auto text-white">
            Google Workspace Business로 팀 생산성을 높이고 비즈니스 성장을 가속화하세요
          </p>
          <div className="flex flex-wrap justify-center gap-6">
            <button 
              onClick={handlePlanSelection}
              className="bg-white text-blue-600 px-10 py-4 rounded-lg font-bold hover:bg-blue-50 transition-all duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            >
              지금 구독하고 시작하기
            </button>
            <a
              href="http://pf.kakao.com/_jyebn/chat"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <button className="bg-transparent text-white px-10 py-4 rounded-lg font-bold border-2 border-white hover:bg-white hover:bg-opacity-10 transition-all duration-300">
                전문가 상담하기
              </button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Business;
